import {
  BarPlot,
  LinePlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
} from "@mui/x-charts";
import { useCfmContext } from "../../../Context/CfmContext";
import "../../../../../../css/CashflowModeller2/Charts/Graphs.css";
import React, { useMemo, useRef } from "react";
import { ChartDataProviderPro } from "@mui/x-charts-pro/ChartDataProviderPro";
import CustomChartWrapper from "../Utils/CustomWrapper";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import useGraphHeight from "../Utils/useGraphHeight";

const CombinedSurplusDeficitGraph = ({
  isPrint = false,
  animated = false,
  apiRef = null,
  idOverride,
}) => {
  const { state } = useCfmContext();
  const chartOnlyRef = useRef();

  const combinedData = useMemo(() => {
    const parseMoney = (v) =>
      typeof v === "number"
        ? v
        : parseFloat(String(v || "0").replace(/[^0-9.-]+/g, "")) || 0;

    // 1) find the earliest DOB and compute the 100th birthday year
    let maxYearAllowed = Infinity;
    if (Array.isArray(state.details) && state.details.length > 0) {
      const dobYears = state.details
        .map((d) => {
          if (!d?.dob) return null;
          const parts = String(d.dob).split(/[-/]/); // support YYYY-MM-DD or DD/MM/YYYY
          if (parts.length < 3) return null;
          const year =
            parts[0].length === 4 ? parseInt(parts[0]) : parseInt(parts[2]);
          return isNaN(year) ? null : year;
        })
        .filter(Boolean);

      if (dobYears.length > 0) {
        const earliestDob = Math.min(...dobYears);
        maxYearAllowed = earliestDob + 100;
      }
    }

    // 2) longest series length across all people
    const maxLen =
      state.incomeExpenditureResults?.reduce((m, entry) => {
        const len = entry?.incomeExpenditure?.length || 0;
        return Math.max(m, len);
      }, 0) || 0;

    // 3) aggregate by index
    const byIdx = Array.from({ length: maxLen }, () => ({
      surplus: 0,
      deficit: 0,
      totalExpenditure: 0,
      totalIncomeAfterTax: 0,
      targetIncome: 0,
    }));

    state.incomeExpenditureResults?.forEach((entry) => {
      entry.incomeExpenditure?.forEach((row, idx) => {
        const b = byIdx[idx];
        if (!b) return;
        const sd = parseMoney(row.surplusDeficit);
        if (sd >= 0) b.surplus += sd;
        else b.deficit += sd;
        b.totalExpenditure += parseMoney(row.totalExpenditure);
        b.totalIncomeAfterTax += parseMoney(row.totalIncomeAfterTax);
        b.targetIncome += parseMoney(row.targetIncome);
      });
    });

    // 4) make years but stop when hitting 100
    const startYear =
      Number(state?.timelineStartYear) || new Date().getFullYear();
    let years = [];
    for (let i = 0; i < maxLen; i++) {
      const yr = startYear + i;
      if (yr > maxYearAllowed) break; // stop once any client hits 100
      years.push(yr);
    }

    // 5) cut down byIdx to same length
    const trimmed = byIdx.slice(0, years.length);

    const surplus = trimmed.map((b) => b.surplus);
    const deficit = trimmed.map((b) => b.deficit);
    const totalExpenditure = trimmed.map((b) => b.totalExpenditure);
    const totalIncomeAfterTax = trimmed.map((b) => b.totalIncomeAfterTax);
    const targetIncome = trimmed.map((b) => b.targetIncome);

    const series = [
      {
        type: "bar",
        data: surplus,
        label: "Combined Surplus",
        color: "#57B5E7",
        stack: "surplus",
      },
      {
        type: "bar",
        data: deficit,
        label: "Combined Deficit",
        color: "#FC8D62",
        stack: "surplus",
      },
      {
        type: "line",
        data: totalExpenditure,
        label: "Combined Total Expenditure",
        color: "#776096",
      },
      {
        type: "line",
        data: totalIncomeAfterTax,
        label: "Combined Total Income After Tax",
        color: "#FBBF72",
      },
      {
        type: "line",
        data: targetIncome,
        label: "Combined Target Income",
        color: "#7AB0D8",
      },
    ];

    const visibleSeries = series.filter((s) =>
      s.data.some((v) => Number(v ?? 0) !== 0)
    );

    return { years, visibleSeries };
  }, [state.incomeExpenditureResults, state?.timelineStartYear, state.details]);


  const graphHeight = useGraphHeight(isPrint);

  return (
    <div
      ref={chartOnlyRef}
      className="cfm-graph-wrapper"
      style={{
        width: isPrint ? 800 : "100%",
        height: isPrint ? 500 : "100%",
        backgroundColor: "transparent",
      }}
      id={idOverride || "combined-sur-def-graph"}
    >
      <ChartDataProviderPro
        apiRef={apiRef}
        key="combined-graph"
        series={combinedData.visibleSeries}
        height={graphHeight}
        fillMode="none"
        xAxis={[
          {
            id: "year-axis",
            data: combinedData.years,
            scaleType: "band",
            label: "Year",
            valueFormatter: (val) => `${val}`,
          },
        ]}
        yAxis={[
          {
            id: "value-axis",
            label: "Amount",
            valueFormatter: (val) => {
              const sign = val < 0 ? "-" : "";
              const absVal = Math.abs(val);
              if (absVal >= 1_000_000)
                return `${sign}£${(absVal / 1_000_000).toFixed(1)}m`;
              if (absVal >= 1_000)
                return `${sign}£${(absVal / 1_000).toFixed(0)}k`;
              return `${sign}£${absVal.toFixed(0)}`;
            },
            width: 70,
          },
        ]}
      >
        <CustomChartWrapper isApiPrint={!!apiRef}>
          <ChartsLegend
            direction="horizontal"
            position="top"
            slotProps={{
              legend: {
                sx: {
                  fontSize: 12,
                  color: "#081e56",
                  fontFamily: "'Open Sans'",
                  fontWeight: 500,
                },
              },
            }}
          />
          <ChartsSurface>
            <BarPlot />
            <LinePlot
              slotProps={{
                line: { style: { strokeWidth: 4 } },
              }}
            />
            <ChartsXAxis
              axisId="year-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsYAxis
              axisId="value-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsTooltip />
          </ChartsSurface>
        </CustomChartWrapper>
      </ChartDataProviderPro>
    </div>
  );
};

export default CombinedSurplusDeficitGraph;
